import type { ButtonVariant } from "@qendo/theme";
import type { ExternalHref } from "@qendo/util";
import { type Href, router } from "expo-router";
import { useCallback, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    type PressableProps,
    StyleSheet,
    type TextStyle,
    View,
    type ViewStyle,
} from "react-native";
import type { ColorValue, StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import type { GestureResponderEvent } from "react-native/Libraries/Types/CoreEventTypes";
import { BaseText } from "~/components/layout/text";
import { QendoIcon, type QendoIconSet } from "~/components/QendoIcon";
import { Radius, Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { isExternalLink, openLink } from "~/util/browser";

export type ButtonSize = "sm" | "md" | "lg";

type NoIcon = {
    title: string;
    icon?: QendoIconSet;
    iconRight?: QendoIconSet;
};
type IconSet = {
    title?: string;
    icon: QendoIconSet;
    iconRight?: QendoIconSet;
};
type IconRightSet = {
    title?: string;
    icon?: QendoIconSet;
    iconRight: QendoIconSet;
};

export type ButtonProps = {
    title?: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
    icon?: QendoIconSet;
    iconRight?: QendoIconSet;
    iconSize?: number;
    colour?: ColorValue;
    background?: ColorValue;
    disabled?: boolean;
    loading?: boolean;
    style?: StyleProp<ViewStyle>;
    innerStyle?: StyleProp<ViewStyle>;
    href?: Href | ExternalHref;
    replace?: boolean;
} & (NoIcon | IconSet | IconRightSet) &
    PressableProps;

export const Button = ({
    title,
    icon,
    iconRight,
    iconSize,
    disabled,
    colour,
    background,
    loading,
    onPress,
    onPressIn,
    onPressOut,
    innerStyle,
    href,
    replace,
    variant = "secondary",
    size = "md",
    ...pressableProps
}: ButtonProps) => {
    const { colours } = useTheme();
    const [pressed, setPressed] = useState(false);

    const handlePress = useCallback(
        (event: GestureResponderEvent) => {
            href && (isExternalLink(href) ? openLink(href) : replace ? router.replace(href) : router.navigate(href));
            onPress?.(event);
        },
        [onPress, href, replace],
    );

    const handlePressIn = useCallback(
        (event: GestureResponderEvent) => {
            setPressed(true);
            onPressIn?.(event);
        },
        [onPressIn],
    );
    const handlePressOut = useCallback(
        (event: GestureResponderEvent) => {
            setPressed(false);
            onPressOut?.(event);
        },
        [onPressOut],
    );

    const text_colour = disabled
        ? colours.button[`${variant}-disabled`]
        : (colour ?? (pressed ? colours.button[`${variant}-pressed`] : colours.button[variant]));

    const background_color = disabled
        ? colours.button[`${variant}-disabled-background`]
        : (background ??
          (pressed ? colours.button[`${variant}-pressed-background`] : colours.button[`${variant}-background`]));

    const icon_size = iconSize ?? iconSizes[size];
    const icon_style = {
        flexShrink: 0,
        width: icon_size,
        height: icon_size,
    };

    const accessibilityRole = pressableProps.accessibilityRole ?? ((filled(href) && "link") || "button");

    const font_size = size === "lg" ? "base" : "md";

    return (
        <Pressable
            {...pressableProps}
            disabled={disabled}
            style={[styles.pressable, { backgroundColor: background_color }, pressableProps.style]}
            onPress={handlePress}
            onPressIn={handlePressIn}
            onPressOut={handlePressOut}
            accessibilityRole={accessibilityRole}
        >
            <View style={[styles.container, container_styles[size], innerStyle]}>
                {filled(icon) && <QendoIcon name={icon} size={icon_size} color={text_colour} style={icon_style} />}
                {filled(title) && (
                    <BaseText
                        size={font_size}
                        weight="semibold"
                        align="center"
                        numberOfLines={1}
                        style={[styles.text, text_styles[size], { color: text_colour }]}
                    >
                        {title}
                    </BaseText>
                )}
                {filled(iconRight) && (
                    <QendoIcon name={iconRight} size={icon_size} color={text_colour} style={icon_style} />
                )}
                {filled(loading) && <ActivityIndicator size="small" />}
            </View>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    pressable: {
        flexDirection: "row",
        justifyContent: "center",
        borderRadius: Radius.md,
        flexShrink: 0,
    },
    container: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
    },
    text: {
        flexShrink: 0,
    },
});

const container_styles = StyleSheet.create<Record<ButtonSize, ViewStyle>>({
    sm: {
        padding: Space.xx,
    },
    md: {
        padding: Space.sm,
    },
    lg: {
        padding: Space.sm,
    },
});

const text_styles = StyleSheet.create<Record<ButtonSize, TextStyle>>({
    sm: {
        paddingHorizontal: Space.xs,
    },
    md: {
        paddingHorizontal: Space.sm,
    },
    lg: {
        paddingHorizontal: Space.sm,
    },
});

const iconSizes = {
    sm: 20,
    md: 20,
    lg: 24,
};
